import { Plus, ChevronDown, Receipt } from 'lucide-react'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'

import { FormModal } from '@components/branded-modal'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { CustomItemForm, ItemList } from '@components/custom-item'
import { AdasBox, AdasTypography } from '@components/wrapper-components'
import { getCustomItemList } from '../../../api/api'
import { currencyFormatter } from '../../../utils/utils'
import { Logger } from '../../../logger'

export const CustomItem = (props) => {
  const {
    existingCustomItemList = [],
    workOrderData,
    onSaveCustomItem,
    customItemTotal,
    disabled = false,
    type,
    defaultExpanded = true,
    title = 'Custom Charges & Discounts',
    showAddButton = true,
    isCustomItem = true
  } = props
  // A client user never adds a charge, on any quote, work order or invoice.
  const isReadOnlyUser = useIsReadOnly()
  const canAdd = showAddButton && !isReadOnlyUser
  const [customItemList, setCustomItemList] = useState([])
  const [isAddCustomItem, setIsAddCustomItem] = useState(false)
  const [existingCustomItem, setExistingCustomItem] = useState()
  const [expanded, setExpanded] = useState(!!defaultExpanded)

  useEffect(() => {
    setCustomItemList(existingCustomItemList)
  }, [existingCustomItemList])

  const addCustomItemHandler = () => {
    setExistingCustomItem()
    setIsAddCustomItem(true)
  }

  const onClickEditHandler = (props) => {
    setExistingCustomItem(props?.product)
    setIsAddCustomItem(true)
  }

  const onSaveSuccessHandler = async () => {
    try {
      setIsAddCustomItem(false)
      const customItemListResponse = await getCustomItemList({
        type: type,
        id: workOrderData.id
      })

      if (customItemListResponse.status === 200) {
        onSaveCustomItem(customItemListResponse.data)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { type, id: workOrderData.id } })
    }
  }

  const itemCount = customItemList.length
  const totalLabel = customItemTotal < 0 ? `-${currencyFormatter(Math.abs(customItemTotal))}` : currencyFormatter(customItemTotal)

  return (
    <AdasBox>
      {isAddCustomItem && (
        <FormModal
          open={isAddCustomItem}
          onClose={() => setIsAddCustomItem(false)}
          title='Custom Work Items & Credits'
          icon={Receipt}
          width='max-w-[520px]'
        >
          <CustomItemForm
            type={type}
            cancel={() => setIsAddCustomItem(false)}
            isNew={!existingCustomItem}
            workOrderData={workOrderData}
            customItemData={existingCustomItem}
            onSaveSuccess={onSaveSuccessHandler}
          />
        </FormModal>
      )}

      <div className='mb-3 overflow-hidden rounded-lg border border-[#D1D5D8] bg-white'>
        <button
          type='button'
          onClick={() => setExpanded((e) => !e)}
          className='flex w-full items-center justify-between gap-3 bg-[#E8EFFF] px-4 py-2.5 text-left'
          aria-expanded={expanded}
        >
          <div className='flex items-center gap-2'>
            <ChevronDown className={`h-4 w-4 text-[#475569] transition-transform ${expanded ? '' : '-rotate-90'}`} strokeWidth={2.25} />
            <span className='text-[14px] font-semibold text-[#0A1628]'>{title}</span>
            <span className='inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#0A1628] px-1.5 text-[11px] font-bold text-white'>
              {itemCount}
            </span>
          </div>
          <span className={`text-[14px] font-semibold ${customItemTotal < 0 ? 'text-destructive' : 'text-[#0A1628]'}`}>{totalLabel}</span>
        </button>

        {expanded && (
          <AdasBox className='px-4 py-3'>
            {itemCount === 0 ? (
              <AdasBox className='flex flex-col items-center justify-center gap-3 py-4'>
                <AdasTypography className='text-[13px] text-[#6B7280]'>No custom charges or discounts yet</AdasTypography>
                {canAdd && (
                  <button
                    type='button'
                    onClick={addCustomItemHandler}
                    disabled={disabled}
                    className='inline-flex items-center gap-1.5 rounded-lg bg-[#0A1628] px-4 py-2 text-[13px] font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#1F2D3D] disabled:cursor-not-allowed disabled:opacity-40'
                  >
                    <Plus className='h-4 w-4' />
                    Add Charge
                  </button>
                )}
              </AdasBox>
            ) : (
              <>
                <ItemList
                  itemList={customItemList}
                  onClickEdit={(props) => onClickEditHandler(props)}
                  name='additional items'
                  isCustomItem={isCustomItem}
                  disabled={disabled}
                  type={type}
                  customData={workOrderData}
                  onSaveSuccess={onSaveSuccessHandler}
                />
                {canAdd && (
                  <button
                    type='button'
                    onClick={addCustomItemHandler}
                    disabled={disabled}
                    className='mt-3 inline-flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-wide text-[#1F6FEB] hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed'
                  >
                    <Plus className='h-4 w-4' />
                    Add Charge
                  </button>
                )}
              </>
            )}
          </AdasBox>
        )}
      </div>
    </AdasBox>
  )
}

CustomItem.propTypes = {
  type: PropTypes.string,
  existingCustomItemList: PropTypes.array,
  workOrderData: PropTypes.object,
  onSaveCustomItem: PropTypes.func,
  customItemTotal: PropTypes.number,
  disabled: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
  title: PropTypes.string,
  showAddButton: PropTypes.bool,
  isCustomItem: PropTypes.bool
}
